import type { CorrectDocumentFieldValue } from '@Application/Beneficiaries/Documents/Commands/CorrectDocumentField';
import isFieldValueDraftValid from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/FormData/constraint/isFieldValueDraftValid';
import isOptionsBasedField from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Helpers/isOptionsBasedField';
import type DocumentFieldView from '@UI/Store/Documents/Views/DocumentFieldView';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import { AbstractValidatedClass, ValidatedClass, constraint } from '@verclam/form-validator';

/** A text, number or date draft is a string; a boolean or a simple choice is a value or `null`; a multiple choice is the list of picked option values. */
export type FieldValueDraft = string | boolean | string[] | null;

type FieldValueFormArgs = {
	fieldTypeValue: string;
	isMultiple: boolean;
	value: FieldValueDraft;
	initialValue: FieldValueDraft;
};

const areDraftsEqual = (left: FieldValueDraft, right: FieldValueDraft): boolean => {
	if (Array.isArray(left) && Array.isArray(right)) {
		return left.length === right.length && left.every((entry) => right.includes(entry));
	}

	return left === right;
};

/**
 * The draft of any field of the document panel — every type goes through it, a choice and a boolean
 * included, so that a single "Enregistrer" / "Annuler" pair protects a single draft. `value` is named
 * after the server's request field (`PUT …/value`): a `400`'s `fieldErrors` attaches to that exact name.
 * `fieldTypeValue` is the control's type: a choice without catalogued options is edited as `text`.
 */
@ValidatedClass()
class FieldValueFormData extends AbstractValidatedClass<FieldValueFormArgs> {
	public readonly fieldTypeValue: string;
	public readonly isMultiple: boolean;
	public readonly initialValue: FieldValueDraft;

	@constraint(isFieldValueDraftValid('date'), () => 'Choisissez une date valide.')
	@constraint(isFieldValueDraftValid('number'), () => 'Saisissez un nombre.')
	public readonly value: FieldValueDraft;

	constructor(data: FieldValueFormArgs) {
		super(data);
		this.fieldTypeValue = data.fieldTypeValue;
		this.isMultiple = data.isMultiple;
		this.initialValue = data.initialValue;
		this.value = data.value;
		Object.freeze(this);
	}

	static createFor(field: DocumentFieldView): FieldValueFormData {
		const fieldTypeValue =
			field.fieldType.value === 'choice' && !isOptionsBasedField(field)
				? 'text'
				: field.fieldType.value;
		const isMultiple = fieldTypeValue === 'choice' && field.isMultiple;
		const initial = FieldValueFormData.readInitialValue(field, fieldTypeValue, isMultiple);

		return new FieldValueFormData({ fieldTypeValue, isMultiple, value: initial, initialValue: initial });
	}

	private static readInitialValue(
		field: DocumentFieldView,
		fieldTypeValue: string,
		isMultiple: boolean,
	): FieldValueDraft {
		const saved = field.value;

		if (fieldTypeValue === 'boolean') {
			return typeof saved === 'boolean' ? saved : null;
		}

		if (fieldTypeValue === 'choice') {
			if (isMultiple) {
				return Array.isArray(saved) ? saved.map((option: LabeledValueView) => option.value) : [];
			}

			return saved instanceof LabeledValueView ? saved.value : null;
		}

		return saved === null || typeof saved === 'object' || typeof saved === 'boolean' ? '' : String(saved);
	}

	get isDirty(): boolean {
		return !areDraftsEqual(this.value, this.initialValue);
	}

	/** `true` when the draft carries nothing: blank text, no pick, no answer. */
	get isBlank(): boolean {
		if (Array.isArray(this.value)) {
			return this.value.length === 0;
		}

		return typeof this.value === 'string' ? this.value.trim() === '' : this.value === null;
	}

	setValue(value: FieldValueDraft): this {
		return this.cloneWith({ value } as Partial<FieldValueFormArgs>);
	}

	/** The draft once saved: it becomes the value the next modification is compared to. */
	markSaved(): this {
		return this.cloneWith({ initialValue: this.value } as Partial<FieldValueFormArgs>);
	}

	/** The empty draft of this field's type. */
	clear(): this {
		if (this.fieldTypeValue === 'boolean' || (this.fieldTypeValue === 'choice' && !this.isMultiple)) {
			return this.setValue(null);
		}

		return this.setValue(this.isMultiple ? [] : '');
	}

	/** The value to send: a blank text → `null`, a number converted, a date `AAAA-MM-JJ`, a choice by its option *value*. */
	toValue(): CorrectDocumentFieldValue {
		const { value } = this;

		if (typeof value !== 'string') {
			return value;
		}

		const trimmed = value.trim();

		if (trimmed === '') {
			return null;
		}

		return this.fieldTypeValue === 'number' ? Number(trimmed) : trimmed;
	}
}

export default FieldValueFormData;
